import React from 'react'
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
} from 'react-router-dom'
import { Analytics } from '@vercel/analytics/react'

import RouterHub from './pages/RouterHub'
import ExperienceSearchPage from './pages/ExperienceSearchPage'
import CheckInDetailPage from './pages/CheckInDetailPage'
import CheckInPassPage from './pages/CheckInPassPage'
import MyExperiencePage from './pages/MyExperiencePage'
import LoginPage from './pages/LoginPage'
import ManageUsersPage from './pages/ManageUsersPage'
import ManageExperiencesPage from './pages/ManageExperiencesPage'
import ManageLocationsPage from './pages/ManageLocationsPage'
import EditUserPage from './pages/EditUserPage'
import ManageStaffPage from './pages/ManageStaffPage'
import EditStaffPage from './pages/EditStaffPage'
import ManageCheckInsPage from './pages/ManageCheckInsPage'
import AdminCheckInsPage from './pages/AdminCheckInsPage'
import OrgSearchPage from './pages/OrgSearchPage'
import EditCheckInPage from './pages/EditCheckinPage'
import EditExperienceDetailsPage from './pages/EditExperienceDetailsPage'
import ManagePortalPage from './pages/ManagePortalPage'
import EditExperienceMessageSetupsPage from './pages/EditExperienceMessageSetupsPage'
import EditExperienceStockTemplatePage from './pages/EditExperienceStockTemplatePage'
import CheckInOverviewPage from './pages/CheckInOverviewPage'
import SalesOverviewPage from './pages/SalesOverviewPage'
import PurchaseDetailPage from './pages/PurchaseDetailPage'
import NotFoundPage from './pages/NotFoundPage'
import ControlOrgsPage from './pages/ControlOrgsPage'
import ControlOrgDetailsPage from './pages/ControlOrgDetailsPage'
import ControlOrgBillingPage from './pages/ControlOrgBillingPage'
import ControlOrgCommercePage from './pages/ControlOrgCommercePage'
import AdminCommercePage from './pages/AdminCommercePage'
import AdminBillingPage from './pages/AdminBillingPage'
import ExperienceUserCheckInHistory from './pages/ExperienceUserCheckInHistory'
import CheckInMessageHistoryPage from './pages/CheckInMessageHistoryPage'
import MessagingCenterPage from './pages/MessagingCenterPage'
import CheckInMessagesPage from './pages/CheckInMessagesPage'
import BlastComposerPage from './pages/BlastComposerPage'
import BlastDetailPage from './pages/BlastDetailPage'
import AccountSettingsPage from './pages/AccountSettingsPage'
import CheckoutCompletePage from './pages/CheckoutCompletePage'
import ExperienceStorePage from './pages/ExperienceStorePage'
import ExperienceStoreConfirmationPage from './pages/ExperienceStoreConfirmationPage'
import ManageExperienceStorePage from './pages/ManageExperienceStorePage'

import PrivateRoute from './components/PrivateRoute'
import ControlRoute from './components/ControlRoute'
import AdminRoute from './components/AdminRoute'
import OwnerRoute from './components/OwnerRoute'

import PrivateLayout from './layouts/PrivateLayout'
import PublicLayout from './layouts/PublicLayout'

import ResetPasswordPage from './pages/ResetPasswordPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'

const App = ({ colorScheme = 'light', toggleColorScheme = () => {} }) => {
	return (
		<Router>
			<Routes>
				{/* Public layout */}
				<Route element={<PublicLayout />}>
					<Route path='/login' element={<LoginPage />} />
					<Route
						path='/get-pass/preview/:experienceId'
						element={<CheckInPassPage experiencePreview />}
					/>
					<Route path='/get-pass/:id' element={<CheckInPassPage />} />
					<Route
						path='/myexperience/preview/:experienceId'
						element={<MyExperiencePage experiencePreview />}
					/>
					<Route
						path='/myexperience/:checkInId'
						element={<MyExperiencePage />}
					/>
					<Route path='/checkout/complete' element={<CheckoutCompletePage />} />
					<Route
						path='/:orgSlug/experiences/:experienceSlug/store'
						element={<ExperienceStorePage />}
					/>
					<Route
						path='/:orgSlug/experiences/:experienceSlug/store/confirmation'
						element={<ExperienceStoreConfirmationPage />}
					/>
					<Route path='/reset-password' element={<ResetPasswordPage />} />
					<Route path='/forgot-password' element={<ForgotPasswordPage />} />
				</Route>

				{/* Private layout + protection */}
				<Route
					element={
						<PrivateRoute>
							<PrivateLayout
								colorScheme={colorScheme}
								toggleColorScheme={toggleColorScheme}
							/>
						</PrivateRoute>
					}
				>
					{/* Basic private routes */}
					<Route index element={<RouterHub />} />
					<Route
						path=':orgSlug/experiences/:experienceSlug'
						element={<ExperienceSearchPage />}
					/>
					<Route
						path=':orgSlug/experiences/:experienceSlug/search'
						element={<Navigate to='/' replace />}
					/>
					<Route
						path=':orgSlug/experiences/:experienceSlug/history'
						element={<ExperienceUserCheckInHistory />}
					/>
					<Route
						path=':orgSlug/experiences/:experienceSlug/checkIn/:id'
						element={<CheckInDetailPage />}
					/>
					<Route
						path=':orgSlug/experiences/:experienceSlug/checkIn/:id/messages'
						element={<CheckInMessageHistoryPage />}
					/>
					<Route
						path=':orgSlug/experiences/:experienceSlug/manage/checkin/:id'
						element={<EditCheckInPage />}
					/>

					<Route
						path=':orgSlug/experiences/:experienceSlug/messaging'
						element={<MessagingCenterPage />}
					/>

					<Route
						path=':orgSlug/experiences/:experienceSlug/messaging/check-in-messages'
						element={<CheckInMessagesPage />}
					/>

					<Route
						path=':orgSlug/experiences/:experienceSlug/messaging/blasts/new'
						element={<BlastComposerPage />}
					/>

					<Route
						path=':orgSlug/experiences/:experienceSlug/messaging/blasts/:id/edit'
						element={<BlastComposerPage />}
					/>

					<Route
						path=':orgSlug/experiences/:experienceSlug/messaging/blasts/:id'
						element={<BlastDetailPage />}
					/>
					<Route path='account/settings' element={<AccountSettingsPage />} />

					<Route path=':orgSlug/search' element={<AdminRoute><OrgSearchPage /></AdminRoute>} />

					{/* Admin-only routes */}
					<Route
						path=':orgSlug/admin'
						element={<Navigate to='experiences' replace />}
					/>
					<Route
						path=':orgSlug/admin/users'
						element={
							<AdminRoute>
								<ManageUsersPage />
							</AdminRoute>
						}
					/>
					<Route
						path=':orgSlug/admin/experiences'
						element={
							<AdminRoute>
								<ManageExperiencesPage />
							</AdminRoute>
						}
					/>
					<Route
						path=':orgSlug/admin/checkins'
						element={
							<AdminRoute>
								<AdminCheckInsPage />
							</AdminRoute>
						}
					/>
					<Route
						path=':orgSlug/admin/locations'
						element={
							<AdminRoute>
								<ManageLocationsPage />
							</AdminRoute>
						}
					/>
					<Route
						path=':orgSlug/admin/commerce'
						element={
							<AdminRoute>
								<AdminCommercePage />
							</AdminRoute>
						}
					/>
					<Route
						path=':orgSlug/admin/billing'
						element={
							<AdminRoute>
								<AdminBillingPage />
							</AdminRoute>
						}
					/>
					<Route
						path=':orgSlug/admin/users/:userId/edit'
						element={
							<AdminRoute>
								<EditUserPage />
							</AdminRoute>
						}
					/>
						<Route
							path=':orgSlug/experiences/:experienceSlug/manage/details'
							element={
								<AdminRoute>
									<EditExperienceDetailsPage />
								</AdminRoute>
							}
						/>
						<Route
							path=':orgSlug/experiences/:experienceSlug/manage/portal'
							element={
								<AdminRoute>
									<ManagePortalPage />
								</AdminRoute>
							}
						/>
						<Route
							path=':orgSlug/experiences/:experienceSlug/manage/store'
							element={
								<AdminRoute>
									<ManageExperienceStorePage />
								</AdminRoute>
							}
						/>
						<Route
							path=':orgSlug/experiences/:experienceSlug/manage/messagesetups'
							element={
								<AdminRoute>
									<EditExperienceMessageSetupsPage />
								</AdminRoute>
							}
						/>
						<Route
							path=':orgSlug/experiences/:experienceSlug/manage/messagesetups/:templateKey'
							element={
								<AdminRoute>
									<EditExperienceStockTemplatePage />
								</AdminRoute>
							}
						/>

					{/* Owner-only routes */}
					<Route
						path=':orgSlug/experiences/:experienceSlug/manage/staff'
						element={
							<OwnerRoute>
								<ManageStaffPage />
							</OwnerRoute>
						}
					/>
					<Route
						path=':orgSlug/experiences/:experienceSlug/manage/staff/:staffId/edit'
						element={
							<OwnerRoute>
								<EditStaffPage />
							</OwnerRoute>
						}
					/>
					<Route
						path=':orgSlug/experiences/:experienceSlug/manage/checkins'
						element={
							<OwnerRoute>
								<ManageCheckInsPage />
							</OwnerRoute>
						}
					/>
					<Route
						path=':orgSlug/experiences/:experienceSlug/overview'
						element={
							<OwnerRoute>
								<CheckInOverviewPage />
							</OwnerRoute>
						}
					/>
					<Route
						path=':orgSlug/experiences/:experienceSlug/overview/sales'
						element={
							<OwnerRoute>
								<SalesOverviewPage />
							</OwnerRoute>
						}
					/>
					<Route
						path=':orgSlug/experiences/:experienceSlug/overview/sales/:saleId'
						element={
							<OwnerRoute>
								<PurchaseDetailPage />
							</OwnerRoute>
						}
					/>
					{/* Super-only routes */}
					<Route
						path='control/orgs'
						element={
							<ControlRoute>
								<ControlOrgsPage />
							</ControlRoute>
						}
					/>
					<Route
						path='control/orgs/:orgSlug/details'
						element={
							<ControlRoute>
								<ControlOrgDetailsPage />
							</ControlRoute>
						}
					/>
					<Route
						path='control/orgs/:orgSlug/billing'
						element={
							<ControlRoute>
								<ControlOrgBillingPage />
							</ControlRoute>
						}
					/>
					<Route
						path='control/orgs/:orgSlug/commerce'
						element={
							<ControlRoute>
								<ControlOrgCommercePage />
							</ControlRoute>
						}
					/>
					<Route
						path='control/orgs/:orgSlug/users'
						element={
							<ControlRoute>
								<ManageUsersPage />
							</ControlRoute>
						}
					/>
					<Route
						path='control/orgs/:orgSlug/experiences'
						element={
							<ControlRoute>
								<ManageExperiencesPage />
							</ControlRoute>
						}
					/>
				</Route>
				<Route path='*' element={<NotFoundPage />} />
			</Routes>
			<Analytics />
		</Router>
	)
}

export default App
